import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { useAuth } from '../auth/AuthProvider'
import { banco } from '../db/banco'
import type { Meta } from '../db/tipos'
import { formatarMoeda } from '../lib/formatos'
import { limitesDoMes } from '../lib/periodo'
import { dataISOLocal } from '../pedidos/precos'
import { resumoComissoes } from '../comissoes/painelDados'
import { realizadoDaMeta } from '../metas/metasDados'
import { calcularRitmo, faltamVenderPorRepresentada, dentroDoPeriodo } from '../metas/calculo'
import { ROTULOS_TIPO_META, formatarValorDeMeta } from '../metas/rotulos'
import { clientesQueNaoCompraram, linhasNaoVendidas } from '../metas/faltamNoPainel'
import {
  resumoDaRota, totalDoMes, variacaoDaComissao, pctRecebido, circuloDasMetas, situacaoDaMeta, contarSituacoes,
  contarPedidosNosEstagios, clientesSemVisitaNaSemana, clientesOuroSemPedidoNoMes, ESTAGIOS_AGUARDANDO_ENTREGA, ESTAGIOS_EM_ABERTO,
  pedacoDoMesAnterior, type SituacaoDaMeta,
} from '../painel/resumoDoInicio'
import { linkDaSituacao } from '../pedidos/filtroDoEndereco'
import BotaoInstalar from '../pwa/BotaoInstalar'
import { useAgora } from '../lib/useAgora'
import { buscarEmpresa, empresaPreenchida } from '../empresa/empresa'
import { rotaDoDia, visitasDeHoje } from '../visitas/logica'
import { compromissosDoDia, textoDosCompromissos, ehAtrasado, nomeDoCliente } from '../compromissos/regras'

interface MetaComRealizado {
  meta: Meta
  realizado: number
  projecao: number
  // Clientes ou linhas, conforme o tipo da meta. Vazio quando nao ha o que
  // dizer -- inclusive nas metas de dinheiro, em que a barra ja mostra o
  // buraco em reais e repetir em texto nao acrescenta (decisao dela).
  faltamNomes: string[]
}

// Tela Inicio ciclo 1 (spec 2026-09-29, aprovada 01/10): Comissao, Metas e
// Pendencias. As contas moram em painel/resumoDoInicio.ts; aqui so se desenha.
const SELO: Record<SituacaoDaMeta, { texto: string; classe: string }> = {
  atingida: { texto: 'Atingida', classe: 'bg-emerald-100 text-emerald-800' },
  em_andamento: { texto: 'Em andamento', classe: 'bg-sky-100 text-sky-800' },
  em_risco: { texto: 'Em risco', classe: 'bg-red-100 text-red-700' },
}

const RAIO = 36
const VOLTA = 2 * Math.PI * RAIO

export default function Painel() {
  const { sair, session } = useAuth()
  // Decisao dela 01/10 (opcao A): as dicas de cada meta abrem ao TOCAR nela.
  const [metaAberta, setMetaAberta] = useState<string | null>(null)
  const [listaAberta, setListaAberta] = useState<'visita' | 'ouro' | null>(null)

  const hoje = new Date()
  const mes = limitesDoMes(hoje.getFullYear(), hoje.getMonth())
  // Decisao dela 01/10: o MESMO PEDACO do mes passado (dia 1 ao mesmo dia).
  const mesAnterior = pedacoDoMesAnterior(hoje)
  const nomeDoMes = hoje.toLocaleDateString('pt-BR', { month: 'long' })

  // ⚠ A CONTA MORA AQUI DENTRO, e nao num gancho de montagem.
  //
  // Esta e a primeira tela que o representante ve ao abrir o app na rua, e o
  // dado do servidor chega DEPOIS dela montar. Lendo uma vez so, ele abre o
  // app, a sincronizacao traz o mes de trabalho dele, e os quatro cartoes de
  // comissao continuam parados nos numeros de antes -- sem nunca se corrigir,
  // ate ele sair da tela e voltar.
  //
  // resumoComissoes cruza lancamentos_comissao com sub_pedidos e pedidos. O
  // Dexie observa as tres, entao a chegada de qualquer uma redesenha. Isso
  // importa alem do atraso: se os lancamentos chegarem ANTES dos sub-pedidos,
  // o cruzamento nao acha o par, pula a linha, e o mes aparece valendo MENOS
  // do que vale.
  const resumo = useLiveQuery(() => resumoComissoes(mes.de, mes.ate), [mes.de, mes.ate])
  // O pedaco do mes anterior so serve para a seta ↑/↓ -- e viva pela mesma razao.
  const resumoAnterior = useLiveQuery(
    () => resumoComissoes(mesAnterior.de, mesAnterior.ate),
    [mesAnterior.de, mesAnterior.ate],
  )

  const regras = useLiveQuery(
    () => banco.regras_comissao.filter((r) => r.apagado_em === null).toArray(),
    [],
  )
  const representadas = useLiveQuery(() => banco.representadas.toArray(), [])

  // As metas E a conta de cada uma na MESMA consulta viva, igual a tela de
  // Metas -- realizadoDaMeta varre pedidos, sub_pedidos e itens_pedido, e a
  // sincronizacao traz os pedidos ANTES dos itens: com leitura unica a barra
  // sairia menor e ficaria assim.
  const metasCalculadas =
    useLiveQuery(async () => {
      const metas = await banco.metas
        .filter(
          (m) =>
            m.apagado_em === null &&
            dentroDoPeriodo(dataISOLocal(new Date()), m.periodo_inicio, m.periodo_fim),
        )
        .toArray()

      const calculadas: MetaComRealizado[] = []
      for (const meta of metas) {
        const realizado = await realizadoDaMeta(meta, new Date())
        const { projecao } = calcularRitmo(realizado, new Date(), meta.periodo_inicio, meta.periodo_fim)

        // So faz sentido numa meta DE MARCA: "quais clientes faltam" nao tem
        // resposta para uma meta pessoal, que atravessa todas as fornecedoras.
        // E so quando ainda falta: meta batida nao vira lista de cobranca.
        const idRep = meta.escopo.startsWith('representada:')
          ? meta.escopo.slice('representada:'.length)
          : null
        let faltamNomes: string[] = []
        if (idRep && realizado < meta.valor_alvo) {
          if (meta.tipo === 'positivacao') {
            faltamNomes = await clientesQueNaoCompraram(idRep, meta.periodo_inicio, meta.periodo_fim)
          } else if (meta.tipo === 'cobertura_linhas') {
            faltamNomes = await linhasNaoVendidas(idRep, meta.periodo_inicio, meta.periodo_fim)
          }
        }

        calculadas.push({ meta, realizado, projecao, faltamNomes })
      }
      return calculadas
    }, []) ?? []

  // As 4 pendencias. Viva tambem: pedidos, marcas, clientes e visitas chegam
  // da sincronizacao depois de a tela montar.
  // Ciclo 2A: compromissos de hoje + a rota de hoje, pelas MESMAS funcoes da
  // aba Agenda. Viva: o dado do servidor chega depois de a tela montar.
  // Relogio vivo (revisao final): com o app aberto de um dia para o outro, o
  // card troca de dia sozinho -- hojeISO e dependencia da consulta.
  const agora = useAgora()
  const hojeISO = dataISOLocal(agora)
  const agendaDeHoje = useLiveQuery(async () => {
    const [doDia, rota, visitas, clientes] = await Promise.all([
      banco.compromissos.where('data').equals(hojeISO).toArray(),
      rotaDoDia(agora.getDay(), agora),
      visitasDeHoje(),
      banco.clientes.toArray(),
    ])
    return { compromissos: compromissosDoDia(doDia, hojeISO), rota: resumoDaRota([...rota.retornos.map((r) => r.cliente), ...rota.doDia], visitas), clientes }
  }, [hojeISO])

  const pendencias = useLiveQuery(async () => {
    const [pedidos, subPedidos, clientes, visitas] = await Promise.all([
      banco.pedidos.toArray(), banco.sub_pedidos.toArray(), banco.clientes.toArray(), banco.visitas.toArray(),
    ])
    return {
      aguardandoEntrega: contarPedidosNosEstagios(pedidos, subPedidos, ESTAGIOS_AGUARDANDO_ENTREGA),
      emAberto: contarPedidosNosEstagios(pedidos, subPedidos, ESTAGIOS_EM_ABERTO),
      semVisita: clientesSemVisitaNaSemana(clientes, visitas, new Date()),
      ouroSemPedido: clientesOuroSemPedidoNoMes(clientes, pedidos, subPedidos, new Date()),
    }
  }, [])

  const nomeRepresentada = (idRep: string) =>
    representadas?.find((r) => r.id === idRep)?.nome ?? ''
  const circulo = circuloDasMetas(metasCalculadas)
  // Decisao dela 01/10: meta fracionada por dia; risco = mais de 20% abaixo.
  const situacoes = contarSituacoes(metasCalculadas, hoje)
  // O tipo da meta e, na de marca, a marca (decisao dela 01/10, c3).
  const nomeDaMeta = (meta: Meta) =>
    ROTULOS_TIPO_META[meta.tipo] +
    (meta.escopo !== 'pessoal' ? ` — ${nomeRepresentada(meta.escopo.slice('representada:'.length))}` : '')
  const totalMes = resumo ? totalDoMes(resumo.totais) : 0
  const variacao =
    resumo && resumoAnterior ? variacaoDaComissao(totalMes, totalDoMes(resumoAnterior.totais)) : null
  const recebidoPct = resumo ? pctRecebido(resumo.totais) : 0
  const nomeCliente = (c: { nome_fantasia: string | null; razao_social: string }) =>
    c.nome_fantasia || c.razao_social

  // Spec 2026-10-06: lembrete discreto ate a razao social ser salva. undefined =
  // carregando (nao pisca na abertura); null = conta sem empresa.
  const usuarioId = session?.user.id
  const empresa = useLiveQuery(() => buscarEmpresa(usuarioId).then((e) => e ?? null), [usuarioId])

  return (
    <section className="p-4 space-y-4">
      {empresa !== undefined && !empresaPreenchida(empresa ?? undefined) && (
        <Link to="/perfil" data-testid="lembrete-empresa"
          className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span aria-hidden="true">🏢</span> Complete os dados da sua empresa <span className="ml-auto" aria-hidden="true">›</span>
        </Link>
      )}
      {resumo && (
        // Desenho dela 02/10 (print): icone redondo, valor grande com a seta num
        // selo, e os 4 numeros numa faixa com divisorias.
        <div data-testid="card-comissao" className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <div className="flex items-start gap-3">
            <span className="w-11 h-11 shrink-0 rounded-full bg-emerald-600 text-white flex items-center justify-center">
              <Icone nome="dinheiro" className="w-6 h-6" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-slate-800">Sua Comissão</h3>
                <Link to="/comissoes" className="text-sm text-teal-700 underline underline-offset-2">ver detalhes ›</Link>
              </div>
              <p className="text-xs text-slate-500">Este mês</p>
              <p data-testid="comissao-do-mes" className="text-3xl font-bold text-teal-900">{formatarMoeda(totalMes)}</p>
              {variacao && (
                <p data-testid="variacao-comissao" className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                  <span
                    className={`rounded-full px-2 py-0.5 text-sm font-bold ${
                      variacao.sentido === 'desce' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}
                  >
                    {variacao.sentido === 'desce' ? '↓' : '↑'} {variacao.pct}%
                  </span>
                  em relação ao mês anterior
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 flex-1 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${recebidoPct}%` }} />
            </div>
            <p data-testid="pct-recebido" className="shrink-0 text-xs text-slate-500">{recebidoPct}% recebido</p>
          </div>
          <div className="grid grid-cols-4 border-t border-slate-100 pt-3 divide-x divide-slate-100">
            <div data-testid="card-previsto" className="px-1">
              <span className="block text-[11px] text-slate-500">Previsto</span>
              <span className="block text-[11px] font-bold text-slate-700">{formatarMoeda(resumo.totais.previsto)}</span>
            </div>
            <div data-testid="card-liberado" className="px-1">
              <span className="block text-[11px] text-slate-500">Liberado</span>
              <span className="block text-[11px] font-bold text-amber-600">{formatarMoeda(resumo.totais.liberado)}</span>
            </div>
            <div data-testid="card-recebido" className="px-1">
              <span className="block text-[11px] text-slate-500">Recebido</span>
              <span className="block text-[11px] font-bold text-emerald-600">{formatarMoeda(resumo.totais.recebido)}</span>
            </div>
            <div data-testid="card-pendente" className="px-1">
              <span className="block text-[11px] text-slate-500">Pendente</span>
              <span className="block text-[11px] font-bold text-slate-700">{formatarMoeda(resumo.totais.pendente)}</span>
            </div>
          </div>
        </div>
      )}

      <div data-testid="card-metas" className="rounded-xl shadow-sm p-3 space-y-3 bg-sky-50 border border-sky-100">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 shrink-0 rounded-full bg-blue-600 text-white flex items-center justify-center">
            <Icone nome="alvo" className="w-6 h-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-slate-800">Minhas Metas</h3>
            <p className="text-xs text-slate-500"><span className="capitalize">{nomeDoMes}</span> · {metasCalculadas.length} {metasCalculadas.length === 1 ? 'meta' : 'metas'}</p>
          </div>
          <Link to="/metas" className="shrink-0 text-sm text-teal-700 underline underline-offset-2">ver detalhes ›</Link>
        </div>
        {metasCalculadas.length === 0 ? (
          <div className="text-center space-y-2">
            <p className="text-sm text-slate-500">Nenhuma meta neste mês</p>
            <Link to="/metas" className="inline-block rounded-lg bg-teal-700 text-white px-4 py-2 text-sm font-semibold">
              Criar metas
            </Link>
          </div>
        ) : (
          <>
            <div className="rounded-lg bg-white p-3 space-y-3">
              {circulo && (
                <div data-testid="circulo-metas" className="flex items-center gap-3">
                  <div className="relative w-24 h-24 shrink-0">
                    <svg viewBox="0 0 88 88" className="anel-3d w-24 h-24 -rotate-90" aria-hidden="true">
                      <defs>
                        <linearGradient id="gradiente-metas" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#10b981" />
                          <stop offset="100%" stopColor="#0B4A47" />
                        </linearGradient>
                      </defs>
                      <circle cx="44" cy="44" r={RAIO} fill="none" stroke="#e2e8f0" strokeWidth="8" />
                      <circle
                        cx="44" cy="44" r={RAIO} fill="none" stroke="url(#gradiente-metas)" strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={VOLTA} strokeDashoffset={(1 - circulo.pct / 100) * VOLTA}
                      />
                    </svg>
                    <span className="miolo-3d absolute inset-[16%] rounded-full" aria-hidden="true" />
                    <span className="absolute inset-0 flex flex-col items-center justify-center leading-tight">
                      <span className="text-xl font-bold text-teal-900">{circulo.pct}%</span>
                      <span className="text-[10px] text-teal-800">alcançado</span>
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <p className="text-xs text-slate-500">
                      <span className="block text-base font-bold text-teal-900">{formatarMoeda(circulo.realizado)}</span>
                      realizados de <span className="font-semibold text-slate-700">{formatarMoeda(circulo.alvo)}</span>
                    </p>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${circulo.pct}%` }} />
                    </div>
                    {circulo.falta > 0 && (
                      <p className="flex items-center gap-2 rounded-md bg-sky-50 px-2 py-1.5 text-[11px] text-slate-500">
                        <Icone nome="grafico" className="w-4 h-4 shrink-0 text-blue-600" />
                        <span>
                          <span className="block text-xs font-bold text-teal-900">Faltam {formatarMoeda(circulo.falta)}</span>
                          para fechar todas as metas
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div data-testid="situacoes-metas" className="grid grid-cols-3 gap-1.5">
                <SeloContagem icone="certo" classe="bg-emerald-50 text-emerald-700" bolinha="bg-emerald-600" n={situacoes.atingida} texto={situacoes.atingida === 1 ? 'atingida' : 'atingidas'} />
                <SeloContagem icone="relogio" classe="bg-amber-50 text-amber-700" bolinha="bg-amber-500" n={situacoes.em_andamento} texto="em andamento" />
                <SeloContagem icone="alerta" classe="bg-red-50 text-red-700" bolinha="bg-red-600" n={situacoes.em_risco} texto="em risco" />
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {metasCalculadas.map(({ meta, realizado, projecao, faltamNomes }) => {
                const situacao = situacaoDaMeta(meta, realizado, hoje)
                const falta = Math.max(0, meta.valor_alvo - realizado)
                const aberta = metaAberta === meta.id
                const temDica =
                  (meta.tipo === 'comissao_necessaria' && falta > 0 && !!regras) || faltamNomes.length > 0
                return (
                  <li key={meta.id} data-testid={`meta-${meta.id}`} className="py-2">
                    <button
                      type="button" onClick={() => setMetaAberta(aberta ? null : meta.id)} aria-expanded={aberta}
                      className="w-full flex items-center justify-between gap-2 text-left text-sm"
                    >
                      <span className="min-w-0 text-slate-700">
                        {nomeDaMeta(meta)}
                        <span className="block text-xs text-slate-500">
                          {formatarValorDeMeta(meta.tipo, realizado)} / {formatarValorDeMeta(meta.tipo, meta.valor_alvo)}
                        </span>
                        <span className="block text-xs text-slate-400">
                          Ritmo atual: fecha em {formatarValorDeMeta(meta.tipo, projecao)}
                        </span>
                      </span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${SELO[situacao].classe}`}>
                        {SELO[situacao].texto}
                      </span>
                    </button>
                    {aberta && (
                      <div className="mt-1">
                        {!temDica && <p className="text-xs text-slate-400">Nenhuma dica para esta meta.</p>}
                        {meta.tipo === 'comissao_necessaria' && falta > 0 && regras && (
                          <div className="text-xs text-slate-500 mt-1">
                            Faltam {formatarMoeda(falta)} de comissão. Para chegar lá, venda mais:
                            {faltamVenderPorRepresentada(falta, regras).map((s) => (
                              <span key={s.representada_id} className="block">
                                • {formatarMoeda(s.vendasNecessarias)} na {nomeRepresentada(s.representada_id)} ({s.pct}%)
                              </span>
                            ))}
                          </div>
                        )}

                        {/* A metade do pedido dela que faltava: a tela dizia QUANTO
                            faltava, nunca A QUEM ir atras. So aparece em positivacao e
                            cobertura_linhas -- nas de dinheiro a barra ja diz o buraco em
                            reais, e repetir em texto nao acrescenta.

                            ⚠ Corta em 3 de proposito: uma marca com 40 clientes encheria
                            a tela do celular, que e onde ela usa isto. */}
                        {faltamNomes.length > 0 && (
                          <div data-testid={`faltam-${meta.id}`} className="text-xs text-slate-500 mt-1">
                            {meta.tipo === 'positivacao' ? 'Não compraram esse mês:' : 'Você não vendeu:'}
                            {faltamNomes.slice(0, 3).map((nomeFalta) => (
                              <span key={nomeFalta} className="block">• {nomeFalta}</span>
                            ))}
                            {faltamNomes.length > 3 && (
                              <span className="block">… e mais {faltamNomes.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </div>

      {pendencias && (
        // Desenho dela 02/10 (print "Painel CRM Multimarca"): card laranja com 4
        // quadrinhos lado a lado. A meta em risco SAIU daqui (fica no card de
        // Metas) e entrou o cliente Ouro sem pedido no mes.
        <div data-testid="card-pendencias" className="rounded-xl shadow-sm p-3 space-y-3 bg-orange-50 border border-orange-100">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 shrink-0 rounded-full bg-orange-500 text-white flex items-center justify-center">
              <Icone nome="sino" />
            </span>
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-800">Pendências</h3>
              <p className="text-xs text-teal-800">Atenção para não perder oportunidades!</p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            <QuadroPendencia
              testId="pendencia-entrega" icone="pedido" cor="red" n={pendencias.aguardandoEntrega}
              titulo={pendencias.aguardandoEntrega === 1 ? 'Pedido pendente' : 'Pedidos pendentes'}
              texto="Aguardando entrega." para={linkDaSituacao(ESTAGIOS_AGUARDANDO_ENTREGA)}
            />
            <QuadroPendencia
              testId="pendencia-visita" icone="cliente" cor="sky" n={pendencias.semVisita.length}
              titulo={pendencias.semVisita.length === 1 ? 'Cliente sem visita' : 'Clientes sem visita'}
              texto="Visitas da semana em atraso."
              aberto={listaAberta === 'visita'} onToque={() => setListaAberta((v) => (v === 'visita' ? null : 'visita'))}
            />
            <QuadroPendencia
              testId="pendencia-ouro" icone="estrela" cor="amber" n={pendencias.ouroSemPedido.length}
              titulo={pendencias.ouroSemPedido.length === 1 ? 'Cliente Ouro' : 'Clientes Ouro'}
              texto="Sem pedido neste mês."
              aberto={listaAberta === 'ouro'} onToque={() => setListaAberta((v) => (v === 'ouro' ? null : 'ouro'))}
            />
            <QuadroPendencia
              testId="pendencia-aberto" icone="pedido" cor="violet" n={pendencias.emAberto}
              titulo={pendencias.emAberto === 1 ? 'Pedido em aberto' : 'Pedidos em aberto'}
              texto="Ainda não enviado." para={linkDaSituacao(ESTAGIOS_EM_ABERTO)}
            />
          </div>
          {/* Os nomes abrem embaixo da grade, na largura toda: dentro de um
              quadrinho de 70px nao cabe nome de cliente. */}
          {listaAberta && (listaAberta === 'visita' ? pendencias.semVisita : pendencias.ouroSemPedido).length > 0 && (
            <ul data-testid={listaAberta === 'visita' ? 'lista-sem-visita' : 'lista-ouro'} className="rounded-lg bg-white p-2 text-xs space-y-1">
              {(listaAberta === 'visita' ? pendencias.semVisita : pendencias.ouroSemPedido).slice(0, 5).map((c) => (
                <li key={c.id}>
                  <Link to={`/clientes/${c.id}`} className="text-teal-700">{nomeCliente(c)}</Link>
                </li>
              ))}
              {(listaAberta === 'visita' ? pendencias.semVisita : pendencias.ouroSemPedido).length > 5 && (
                <li className="text-slate-500">e mais {(listaAberta === 'visita' ? pendencias.semVisita : pendencias.ouroSemPedido).length - 5}</li>
              )}
            </ul>
          )}
        </div>
      )}

      {agendaDeHoje && (
        <div data-testid="card-agenda" className="bg-white rounded-xl shadow-sm p-3 space-y-3">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 shrink-0 rounded-full bg-teal-700 text-white flex items-center justify-center">
              <Icone nome="calendario" className="w-6 h-6" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-slate-800">Minha Agenda</h3>
              <p className="text-xs text-slate-500">Hoje · {textoDosCompromissos(agendaDeHoje.compromissos.length)}</p>
            </div>
            <Link to="/compromissos" className="shrink-0 text-sm text-teal-700 underline underline-offset-2">ver agenda ›</Link>
          </div>
          {agendaDeHoje.compromissos.length === 0 ? (
            <div className="text-center space-y-2">
              <p className="text-sm text-slate-500">Nenhum compromisso hoje</p>
              <Link to="/compromissos/novo" className="inline-block rounded-lg bg-teal-700 text-white px-4 py-2 text-sm font-semibold">
                + Compromisso
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {agendaDeHoje.compromissos.slice(0, 3).map((c) => {
                const cliente = nomeDoCliente(agendaDeHoje.clientes, c.cliente_id)
                const atrasado = ehAtrasado(c, agora)
                return (
                  <li key={c.id}>
                    <Link to={`/compromissos/${c.id}`} data-testid={`agenda-${c.id}`} className="flex items-center gap-2 py-2">
                      <span className={`w-2 h-2 shrink-0 rounded-full ${c.situacao === 'feito' ? 'bg-emerald-500' : atrasado ? 'bg-red-500' : 'bg-slate-400'}`} />
                      <span className="w-11 shrink-0 text-sm font-semibold text-slate-800">{c.hora}</span>
                      <span className="min-w-0 flex-1 text-sm text-slate-700 truncate">
                        {c.assunto}
                        {cliente && <span className="block text-xs text-slate-500 truncate">{cliente}</span>}
                      </span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
                        c.situacao === 'feito' ? 'bg-emerald-100 text-emerald-800' : atrasado ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
                        {c.situacao === 'feito' ? 'Feito' : 'Pendente'}
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
          {agendaDeHoje.compromissos.length > 3 && (
            <Link to="/compromissos" className="block text-center text-sm text-teal-700">ver todos ({agendaDeHoje.compromissos.length})</Link>
          )}
          <Link to="/agenda" data-testid="faixa-rota" className="flex items-center justify-between rounded-lg bg-sky-50 px-3 py-2 text-sm text-slate-700">
            <span>
              📍 {agendaDeHoje.rota.total === 0
                ? 'Nenhum cliente na rota de hoje'
                : `Rota de hoje: ${agendaDeHoje.rota.total} ${agendaDeHoje.rota.total === 1 ? 'cliente' : 'clientes'}, ${agendaDeHoje.rota.visitados} ${agendaDeHoje.rota.visitados === 1 ? 'visitado' : 'visitados'}`}
            </span>
            <span aria-hidden="true">›</span>
          </Link>
        </div>
      )}

      {/* O atalho de importar SAIU daqui em 16/08/2026, decisao dela: importar
          e coisa de montar o catalogo, feita uma vez, e nao trabalho de todo
          dia -- ocupava a primeira tela do representante sem merecer. Ele
          continua em Mais → 📥 Importar planilha, que e o caminho que o guia
          ensina (src/ajuda/conteudo.ts). Nao pode voltar para ca sem ela pedir.
          Ha E2E cobrando os dois lados (importar-so-no-mais.spec.ts). */}

      {/* Spec 2026-09-29: o botao de instalar foi para o fim. Some sozinho depois de instalado. */}
      <BotaoInstalar />

      {/* Discreto de proposito, mas sempre no mesmo lugar: e a resposta de
          "qual versao voce esta usando?" durante o teste com os representantes. */}
      <p className="pt-2 text-center text-xs text-slate-400" data-testid="versao-app">
        versão {__VERSAO_APP__}
      </p>
      {/* O Sair so existe aqui (dois E2E dependem dele). */}
      <div className="text-center">
        <button onClick={sair} className="text-sm text-teal-700 underline">Sair</button>
      </div>
    </section>
  )
}

// Icones de traco (desenho dela 02/10). Inline: o app nao tem biblioteca de icones.
const TRACOS: Record<'calendario' | 'sino' | 'pedido' | 'cliente' | 'estrela' | 'dinheiro' | 'alvo' | 'grafico' | 'certo' | 'relogio' | 'alerta', string[]> = {
  dinheiro: ['M12 2v20', 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
  alvo: ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z', 'M12 6a6 6 0 1 0 0 12 6 6 0 0 0 0-12z', 'M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z'],
  grafico: ['M6 20v-6', 'M12 20V10', 'M18 20V4'],
  certo: ['M20 6 9 17l-5-5'],
  relogio: ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z', 'M12 6v6l4 2'],
  alerta: ['M12 7v6', 'M12 17h.01'],
  calendario: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z'],
  sino: ['M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9', 'M10.3 21a1.94 1.94 0 0 0 3.4 0'],
  pedido: ['M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z', 'M14 2v5h5', 'M9 13h6', 'M9 17h6'],
  cliente: ['M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2', 'M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z'],
  estrela: ['M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z'],
}

function Icone({ nome, className = 'w-5 h-5' }: { nome: keyof typeof TRACOS; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      {TRACOS[nome].map((d) => <path key={d} d={d} />)}
    </svg>
  )
}

// As cores de cada quadrinho, como no print. Escritas por inteiro: o Tailwind
// so gera a classe que encontra literal no codigo.
const CORES = {
  red: { fundo: 'bg-red-100 text-red-600', numero: 'text-red-600' },
  sky: { fundo: 'bg-sky-100 text-sky-700', numero: 'text-sky-700' },
  amber: { fundo: 'bg-amber-100 text-amber-500', numero: 'text-amber-600' },
  violet: { fundo: 'bg-violet-100 text-violet-700', numero: 'text-violet-700' },
}

function QuadroPendencia({ testId, icone, cor, n, titulo, texto, para, aberto, onToque }: {
  testId: string; icone: keyof typeof TRACOS; cor: keyof typeof CORES; n: number; titulo: string; texto: string
  para?: string; aberto?: boolean; onToque?: () => void
}) {
  // Zero fica apagado, mas no lugar: os 4 quadrinhos nunca mudam de posicao.
  const conteudo = (
    <>
      <span className="flex items-center gap-1">
        <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center ${CORES[cor].fundo}`}>
          <Icone nome={icone} className="w-4 h-4" />
        </span>
        <span className={`text-lg font-bold ${CORES[cor].numero}`}>{n}</span>
      </span>
      {/* Pedido dela 02/10: titulo SEMPRE em 2 linhas (1a palavra em cima), para
          os 4 quadrinhos ficarem alinhados -- "Clientes Ouro" cabia numa linha so. */}
      <span className="block mt-1 text-[11px] font-semibold leading-tight text-slate-800">
        {titulo.split(' ')[0]}{' '}<br />{titulo.split(' ').slice(1).join(' ')}
      </span>
      <span className="block mt-0.5 text-[10px] leading-tight text-slate-500">{texto}</span>
      <span className="block mt-auto pt-1 text-sm leading-none text-slate-600" aria-hidden="true">›</span>
    </>
  )
  const classe = `flex flex-col min-w-0 rounded-lg bg-white shadow-sm p-1.5 text-left ${n === 0 ? 'opacity-50' : ''}`
  if (para) return <Link to={para} data-testid={testId} className={classe}>{conteudo}</Link>
  return (
    <button type="button" data-testid={testId} onClick={onToque} aria-expanded={aberto} disabled={n === 0} className={classe}>
      {conteudo}
    </button>
  )
}

// Os 3 selos de situacao das metas (desenho dela 02/10).
function SeloContagem({ icone, classe, bolinha, n, texto }: {
  icone: keyof typeof TRACOS; classe: string; bolinha: string; n: number; texto: string
}) {
  return (
    <div className={`flex items-center gap-1 rounded-lg px-1 py-1.5 ${classe}`}>
      <span className={`w-5 h-5 shrink-0 rounded-full text-white flex items-center justify-center ${bolinha}`}>
        <Icone nome={icone} className="w-3 h-3" />
      </span>
      <span className="min-w-0 leading-tight">
        <span className="block text-base font-bold">{n}</span>
        <span className="block text-[10px] whitespace-nowrap">{texto}</span>
      </span>
    </div>
  )
}
